import { motion } from "framer-motion";
import { ArrowDownRight, Play } from "lucide-react";

const reveal = {
  initial: { y: 40, opacity: 0 },
  animate: { y: 0, opacity: 1 },
};

// Masked line-by-line reveal for the hero headline.
const HeroLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1], delay }}
      className={`block will-change-transform ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center px-6 md:px-16 lg:px-32 pt-32 pb-16 overflow-hidden items-center text-center"
    >
      {/* Neon glow, hidden on small screens to reduce paint cost */}
      <div className="hidden md:block absolute -top-32 -right-32 w-[600px] h-[600px] neon-glow-pink neon-pulse pointer-events-none" />
      <div className="hidden md:block absolute bottom-0 -left-32 w-[500px] h-[500px] neon-glow-gold pointer-events-none" />

      <div className="relative z-10 max-w-7xl">
        <motion.div
          {...reveal}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="flex justify-center mb-12 md:mb-16"
        >
          <span
            data-testid="hero-badge"
            className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#F4C75E] to-[#C9963C] px-6 py-2.5 shadow-[0_0_35px_rgba(244,199,94,0.45)]"
          >
            <span aria-hidden className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-black" />
            </span>
            <span
              className="font-display text-xs md:text-sm uppercase text-black font-semibold"
              style={{ letterSpacing: "0.09em", wordSpacing: "0.22em" }}
            >
              Podcasting for founders and businesses
            </span>
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.15 }}
          className="font-display font-bold uppercase leading-[0.9] tracking-tight text-[2.5rem] md:text-5xl lg:text-6xl [word-spacing:0.08em] max-w-5xl mx-auto"
        >
          <HeroLine delay={0.25}>Turn Each Recording</HeroLine>
          <HeroLine delay={0.42} className="text-[#F4C75E]">
            Into A Media Engine.
          </HeroLine>
        </motion.h1>

        <motion.div
          {...reveal}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
          className="mt-6 md:mt-8 flex flex-col items-center gap-8"
        >
          <p className="text-base md:text-lg text-[#A3A3A3] leading-relaxed max-w-xl text-center">
            Great conversations deserve{" "}
            <span className="text-[#F5F5F5]">great distribution.</span>&nbsp;End to end
            post production and distribution for founders and businesses to match the
            caliber of your conversations.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://calendly.com/darshan081200/30min"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-call-button"
              className="group bg-[#F4C75E] text-black font-bold uppercase tracking-[0.2em] text-xs md:text-sm px-8 py-5 hover:bg-white hover:text-black transition-all duration-300 shadow-[0_0_30px_rgba(244,199,94,0.35)] hover:shadow-[0_0_40px_rgba(255,255,255,0.5)] inline-flex items-center justify-center gap-3"
            >
              Work With Us
              <ArrowDownRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <a
              href="#work"
              data-testid="hero-view-work-button"
              className="group bg-transparent border border-[#D4AF37] text-[#D4AF37] font-bold uppercase tracking-[0.2em] text-xs md:text-sm px-8 py-5 hover:bg-[#D4AF37] hover:text-black transition-all duration-300 inline-flex items-center justify-center gap-3"
            >
              <Play className="w-4 h-4 fill-current" />
              View Work
            </a>
          </div>
        </motion.div>

        <motion.div
          {...reveal}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.6 }}
          className="mt-16 md:mt-24 grid grid-cols-3 gap-6 md:gap-12 max-w-2xl mx-auto"
        >
          {[
            { v: "100+", l: "Episodes Produced" },
            { v: "100%", l: "Publish Ready" },
            { v: "<72hr", l: "Turnaround" },
          ].map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`} className="border-l border-[#222] pl-4 md:pl-6">
              <div className="font-display text-xl md:text-3xl font-bold tracking-tight">
                {s.v}<span className="text-[#F4C75E]">.</span>
              </div>
              <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#666] mt-2">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
